import { useContext, useMemo } from 'react';

import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	createFabricMentionResource,
	XProductUserInviteContext,
} from '@confluence/fabric-media-support/entry-points/MentionsProvider.ts';
import { CoreInvitesContext } from '@confluence/core-invites-provider/entry-points/CoreInvitesContext.ts';
import { CoreInvitesSource } from '@confluence/core-invites-provider/entry-points/constants.ts';
import { fg } from '@confluence/feature-gating';
import { usePageContentId } from '@confluence/page-context';
import { usePageTitle } from '@confluence/content-title/entry-points/usePageTitle.ts';

import { useSpaceIdPromise } from './useSpaceIdPromise.ts';

export function useMentionProvider(
	spaceId: string | null | undefined,
	withAnalyticsEventProps?: WithAnalyticsEventsProps,
	focusedMentionId?: string,
	options?: {
		includeAgents?: boolean;
	},
) {
	const { cloudId, userId } = useSessionData();
	const { openCoreInvites, userRole } = useContext(CoreInvitesContext);
	const { isEligibleXProductUserInvite, onXProductUserInviteClick } =
		useContext(XProductUserInviteContext); // check if user is eligible for XProductUserInvite

	const [contentId] = usePageContentId();
	const { title: pageTitle } = usePageTitle({ contentId: contentId || '' });

	const spaceIdPromise = useSpaceIdPromise(spaceId);

	return useMemo(
		() => {
			const onInviteItemClick = openCoreInvites
				? () => {
						openCoreInvites(
							CoreInvitesSource.MENTION,
							undefined,
							[],
							pageTitle ? { pageTitle } : undefined,
						);
					}
				: undefined;

			return spaceIdPromise.then((resolvedSpaceId) => {
				return createFabricMentionResource(
					cloudId,
					resolvedSpaceId,
					userId,
					withAnalyticsEventProps,
					fg('twcg-1239-contextual-ra-note-gate') ? onInviteItemClick : openCoreInvites,
					userRole,
					focusedMentionId,
					isEligibleXProductUserInvite,
					onXProductUserInviteClick,
					// Include agent mentions
					Boolean(options?.includeAgents),
				);
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			cloudId,
			userId,
			withAnalyticsEventProps,
			openCoreInvites,
			userRole,
			isEligibleXProductUserInvite,
			onXProductUserInviteClick,
			options?.includeAgents,
		],
	);
}
